/**
 * Fetches every page of a Spring `Page` endpoint and returns all of their `content`, in order. Used by table exports,
 * which must contain every row rather than the page on screen.
 *
 * `fetchPage({ page, size })` must resolve to a Spring page (`content`, `last`, `totalPages`). The default page size
 * stays under Spring's default `max-page-size` of 2000, above which a request is silently clamped — a larger size
 * would still "work" while quietly returning fewer rows per page than asked for.
 *
 * Standalone (imports nothing from the `utils` barrel, which contains JSX) so vitest can import it.
 */
export const DEFAULT_EXPORT_PAGE_SIZE = 1000;

/** Stops a response that never reports its last page from looping forever (1000 × 1000 = a million rows). */
export const MAX_EXPORT_PAGES = 1000;

export async function fetchAllPages(fetchPage, { pageSize = DEFAULT_EXPORT_PAGE_SIZE, maxPages = MAX_EXPORT_PAGES } = {}) {
  const rows = [];
  for (let page = 0; page < maxPages; page++) {
    const result = await fetchPage({ page, size: pageSize });
    const content = result?.content ?? [];
    rows.push(...content);
    const totalPages = result?.totalPages;
    const reachedEnd =
      result?.last === true || content.length === 0 || (Number.isFinite(totalPages) && page + 1 >= totalPages);
    if (reachedEnd) return rows;
  }
  throw new Error(`Export stopped after ${maxPages} pages`);
}

/** A translated header for a spreadsheet cell: markup such as `tCO<sub>2e</sub>` flattened to `tCO2e`. */
export const plainExportHeader = (translated) => String(translated ?? "").replace(/<[^>]+>/g, "");
